import { useRef, useState } from "react"
import { faArrowUpRightFromSquare, faChevronLeft, faPlus } from "@fortawesome/free-solid-svg-icons"
import Modal from "@/components/modal"
import LaunchButton from "@/components/LaunchButton"
import Button from "@/components/button"
import ListItem from "@/components/list-item"
import Link from "@/components/link"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import documentStore from "../../model/document.store"
import { createEmptyDocument, Document } from "../../model/document.types"
import { getDocumentYear } from "../../utils/document-year.utils"
import { getPublicDocumentPath, openUrl } from "../../utils/document-file.utils"
import LoadingModal from "@/modals/loading.modal"
import CongratulationModal from "@/modals/congratulation.modal"
import { toast } from "react-toastify"

export default function PapripactGenerateModal({ onClose }: { onClose: () => void }) {
  const dispatch = useAppDispatch()
  const establishment = useAppSelector((state) => state.establishment.active)
  const session = useAppSelector((state) => state._session)
  const activeColor = session.accountColors.active
  const documents = useAppSelector(documentStore.selectors.list)
  const currentYear = new Date().getFullYear()
  // During the last 3 months of the year, the next program is the one being prepared
  const [year, setYear] = useState(new Date().getMonth() >= 9 ? currentYear + 1 : currentYear)
  // N+1 down to N-8, older years loaded on demand
  const [yearCount, setYearCount] = useState(10)
  const [step, setStep] = useState<"YEAR" | "CHOICE">("YEAR")
  const [created, setCreated] = useState<Document | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const saving = useRef(false)
  const establishmentId = session.establishmentId ?? establishment.id
  const existing = documents
    .filter(
      (document) =>
        document.type === "papripact" &&
        document.EstablishmentId === establishmentId &&
        getDocumentYear(document) === year
    )
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0]

  async function generate() {
    if (saving.current) return
    saving.current = true
    setIsSaving(true)
    try {
      const now = new Date()
      // Keep the loader at least 250ms so the user sees something happen
      const [document] = await Promise.all([
        dispatch(
          documentStore.thunks.createEntity(
            createEmptyDocument({
              type: "papripact",
              date: now,
              EstablishmentId: establishmentId,
              content: {
                programYear: year,
                generatedAt: now.toISOString(),
                isPlaceholder: true,
                accountName: session.accountName,
                establishment: { id: establishment.id, name: establishment.name }
              }
            })
          )
        ).unwrap(),
        new Promise((resolve) => setTimeout(resolve, 250))
      ])
      setCreated(document)
    } catch {
      toast("Le PAPRIPACT n'a pas pu être créé. Vous pouvez réessayer.", { type: "error" })
    } finally {
      saving.current = false
      setIsSaving(false)
    }
  }

  if (isSaving) return <LoadingModal />

  if (created) {
    return (
      <CongratulationModal
        text={`Votre PAPRIPACT ${year} a bien été généré.`}
        onNext={() => {
          openUrl(getPublicDocumentPath(created))
          onClose()
        }}
        onNextText="Ouvrir le PAPRIPACT"
        onClose={onClose}
        onCloseText="Fermer"
      />
    )
  }

  return (
    <Modal
      title={step === "YEAR" ? "Créer le PAPRIPACT" : `PAPRIPACT ${year}`}
      isCloseButtonVisible
      onClose={onClose}
      onNext={
        step === "YEAR"
          ? () => {
              if (existing) setStep("CHOICE")
              else generate()
            }
          : undefined
      }
      onNextCta="Continuer"
      footerStart={
        step === "CHOICE" && (
          <Button icon={faChevronLeft} onClick={() => setStep("YEAR")}>
            Retour
          </Button>
        )
      }>
      {step === "YEAR" ? (
        <div>
          <div className="font-bold">Pour quelle année souhaitez-vous créer le PAPRIPACT ?</div>
          <div className="grey-t mt-[4px] mb-20">
            Le PAPRIPACT (Programme annuel de prévention des risques professionnels et d’amélioration des conditions de
            travail) liste les actions de prévention prévues sur l’année, à partir des risques identifiés dans votre
            document unique.
          </div>
          {/* Fixed height of 3 rows (64px each) */}
          <div className="modal-content-list h-[192px] overflow-y-auto">
            {Array.from({ length: yearCount }, (_, index) => currentYear + 1 - index).map((option) => (
              <ListItem
                isEditable
                key={option}
                isActive={option === year}
                // bgColor also paints the separator: keep the default list-item grey
                className="!border-t-[#F0F0F0]"
                bgColor={option === year ? `color-mix(in srgb, ${activeColor} 8%, white)` : null}
                onClick={() => setYear(option)}>
                {option === year && (
                  <div className="absolute left-0 top-0 bottom-0 w-10" style={{ backgroundColor: activeColor }} />
                )}
                <div className={`flex1 text-center text-[18px] leading-[27px] ${option === year ? "font-bold" : ""}`}>
                  {option}
                </div>
              </ListItem>
            ))}
            <div className="flex justify-center">
              <Link onClick={() => setYearCount(yearCount + 10)}>Afficher plus d’années</Link>
            </div>
          </div>
        </div>
      ) : (
        // Fixed width so both button titles wrap on 2 lines
        <div className="w-[500px]">
          <div className="mb-20">
            <b>Un PAPRIPACT existe déjà pour l’année {year}.</b>
          </div>
          <div className="flex gap-[12px]">
            <LaunchButton
              title="Consulter le document existant"
              icon={faArrowUpRightFromSquare}
              onClick={() => {
                if (!existing) return
                openUrl(getPublicDocumentPath(existing))
                onClose()
              }}
            />
            <LaunchButton title="Créer un nouveau PAPRIPACT" icon={faPlus} onClick={generate} />
          </div>
        </div>
      )}
    </Modal>
  )
}
